/**
 * When the Jeugdwet surfaces appear inside a real screen (#4659, #4857).
 *
 * A view mounts `<JeugdwetSlot name="…">` at one named place and passes the record it is
 * already showing; everything deciding whether anything renders there lives here.
 * Qualification reads a **real** property — the zorgtraject's `financingRegime` — never a
 * seeded id, so the Jeugdwet layout follows any Jeugdwet record rather than one child.
 *
 * The organisation's own enablement in Organisatiebeheer › Bekostiging (#4697) is the outer
 * gate: without JEUGDWET no slot qualifies, whatever its context. The toggle decides what a
 * practice *can* set up, the record what appears in a dossier.
 */
import { computed, type ComputedRef } from 'vue'
import { useFinancingRegimes } from '@/composables/useFinancingRegimes'
import type { CareCircleContactResponse, ClientProfile } from '@/api/generated-clients'
import type { ZorgtrajectResponse } from '@/api/generated-zorgtrajecten'

/** Only the field qualification reads, so a caller holding a narrower shape can ask too. */
type RegimeCarrier = Pick<ZorgtrajectResponse, 'financingRegime'>

/** The record each slot is mounted beside, and which is handed on to what it renders. */
export interface JeugdwetSlotContexts {
  'zorgtraject-detail': { zorgtraject: ZorgtrajectResponse }
  /** The close dialog, with the closing date it proposes as each stop date (#4816). */
  'zorgtraject-afsluiten': { zorgtraject: ZorgtrajectResponse; einddatum: string }
  zorgkring: {
    client: Pick<ClientProfile, 'id'>
    zorgtrajecten: readonly RegimeCarrier[]
    /** The tab's own contacts bar the huisarts and specialist, so both list the same people. */
    contacts: readonly CareCircleContactResponse[]
  }
  /** Every zorgtraject the cliënt has, ended ones too: a previous toewijzing sits on one (#4809). */
  'client-toewijzingen': { zorgtrajecten: readonly ZorgtrajectResponse[] }
  /** Every cliënt: a bericht can predate any zorgtraject, so the surface itself decides. */
  'client-berichtenverkeer': {
    client: Pick<ClientProfile, 'id'>
    zorgtrajecten: readonly RegimeCarrier[]
  }
}

/** The named places a view can mount a Jeugdwet surface; closed, so an unknown name does not compile. */
export type JeugdwetSlotName = keyof JeugdwetSlotContexts

/** Null is "never recorded" and is not Jeugdwet — nothing infers a regime from absence (ADR-030). */
export function isJeugdwetTrajectory(zorgtraject: RegimeCarrier): boolean {
  return zorgtraject.financingRegime === 'JEUGDWET'
}

/** A cliënt qualifies through any one of their zorgtrajecten. */
export function hasJeugdwetTrajectory(zorgtrajecten: readonly RegimeCarrier[]): boolean {
  return zorgtrajecten.some(isJeugdwetTrajectory)
}

/** What the organisation is enabled for, as {@link useFinancingRegimes} answers it. */
export interface RegimeEnablement {
  jeugdwet: boolean
}

const qualifies: {
  [N in JeugdwetSlotName]: (context: JeugdwetSlotContexts[N] | undefined) => boolean
} = {
  'zorgtraject-detail': (context) => context !== undefined && isJeugdwetTrajectory(context.zorgtraject),
  'zorgtraject-afsluiten': (context) => context !== undefined && isJeugdwetTrajectory(context.zorgtraject),
  zorgkring: (context) => context !== undefined && hasJeugdwetTrajectory(context.zorgtrajecten),
  'client-toewijzingen': (context) => context !== undefined && hasJeugdwetTrajectory(context.zorgtrajecten),
  'client-berichtenverkeer': (context) => context !== undefined
}

export function qualifiesForJeugdwet<N extends JeugdwetSlotName>(
  name: N,
  context: JeugdwetSlotContexts[N] | undefined,
  enabled: RegimeEnablement
): boolean {
  return enabled.jeugdwet && qualifies[name](context)
}

/**
 * Whether the organisation has Jeugdwet enabled at all — the gate every entry point outside a
 * slot shares (a tab, a menu item, a route), and the one a slot starts from. Until the
 * organisation is loaded, Jeugdwet is off.
 */
export function useJeugdwetEnablement(): {
  enabled: ComputedRef<RegimeEnablement>
  ensureLoaded: () => Promise<void>
} {
  const { isRegimeEnabled, ensureLoaded } = useFinancingRegimes()
  void ensureLoaded()

  const enabled = computed<RegimeEnablement>(() => ({ jeugdwet: isRegimeEnabled('JEUGDWET') }))
  return { enabled, ensureLoaded }
}
